"use client";

import React from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, PhoneCall, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Camberley?",
  answer: "Yes. BMF Scaffolding regularly covers Camberley along with surrounding towns across Surrey, Berkshire, and Hampshire, including Frimley, Bagshot, Lightwater, and Sandhurst."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Camberley?",
  answer: "We provide full scaffolding solutions including residential access, commercial scaffolds, new build structures, refurbishment access, temporary roofs, edge protection, chimney stacks, and solar panel installation scaffolding."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We design and erect scaffolding specifically tailored for roof repairs, complete re-roofs, fascia replacements, solar arrays, chimney maintenance, and full temporary weather-protection roofs."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We work directly with main contractors, developers, commercial clients, local authorities, and roofing specialists, as well as individual homeowners and private builders."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. We regularly install bespoke access solutions for narrow side passages, sloping ground, restricted driveways, over conservatories, and internal courtyards using compliant engineering and tailored configurations."
}, {
  id: "item-6",
  question: "How do I request a quote in Camberley?",
  answer: "You can submit project details, architectural drawings, or site photos through our online quote request form, or contact our team directly by phone to arrange a site visit."
}];
export default function FaqCamberley() {
  return <section id="faqcamberley" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Context & Callout */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 text-xs gap-1.5 font-medium border-border">
                <HelpCircle className="size-3.5 text-primary" />
                Camberley Access
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Frequently asked questions about scaffolding in Camberley
            </h2>

            <p className="text-muted-foreground text-base leading-relaxed">
              Find factual answers to common questions about our scaffolding hire,
              safety standards, and access capabilities throughout Camberley and surrounding Surrey areas.
            </p>

            <div className="rounded-xl border border-border bg-card p-6 shadow-sm mt-2">
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-primary/10 p-2.5 text-primary shrink-0">
                  <ShieldCheck className="size-5" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-semibold text-sm text-card-foreground">
                    Accredited scaffolding contractor
                  </h3>
                  <p className="text-xs text-muted-foreground leading-normal">
                    Fully accredited by NASC and CHAS, upholding strict safety protocols on every residential and commercial project.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-border flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <PhoneCall className="size-4 text-primary shrink-0" />
                  <span>Need specific advice?</span>
                </div>
                <Button asChild size="sm" variant="default" className="shadow-xs">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5">
                    <span>Request a quote</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Accordion */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {faqs.map((faq, index) => <div key={faq.id} data-index={index}>
                    <AccordionItem value={faq.id} className="border-border py-1">
                      <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-card-foreground hover:text-primary transition-colors py-4">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-5">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}